import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Instagram, Facebook, MessageCircle, Check, Loader2, AlertCircle, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Channels = () => {
  const { toast } = useToast();
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [channels, setChannels] = useState({
    instagram: { status: 'not_connected', username: null },
    messenger: { status: 'not_connected', page_name: null },
    whatsapp: { status: 'coming_soon' }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [connectingChannel, setConnectingChannel] = useState(null);
  const [disconnectingChannel, setDisconnectingChannel] = useState(null);

  // Handle OAuth callback results
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success');
    const oauthError = searchParams.get('oauth_error');
    const username = searchParams.get('username');
    const errorCase = searchParams.get('case');
    const message = searchParams.get('message');

    if (oauthSuccess === 'true') {
      toast({
        title: 'Successfully Connected!',
        description: username ? 'Connected to @' + username : 'Channel connected successfully.'
      });
      setSearchParams({});
      fetchChannelsStatus();
    } else if (oauthError) {
      let errorMessage = message || 'Connection failed';
      if (errorCase === 'B') {
        errorMessage = 'No Instagram Business Account found. Please link your Instagram to a Facebook Page.';
      } else if (errorCase === 'C') {
        errorMessage = 'No Facebook Pages found. You need admin access to at least one Facebook Page.';
      }
      toast({
        title: 'Connection Failed',
        description: errorMessage,
        variant: 'destructive'
      });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, toast]);

  useEffect(() => {
    fetchChannelsStatus();
  }, []);

  const fetchChannelsStatus = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_URL + '/api/channels/status', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (response.ok) {
        const data = await response.json();
        setChannels(data);
      }
    } catch (error) {
      console.error('Error fetching channels status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (channelType) => {
    setConnectingChannel(channelType);
    try {
      const response = await fetch(API_URL + '/api/channels/' + channelType + '/connect', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.oauth_url;
      } else {
        toast({
          title: 'Connection Failed',
          description: 'Unable to initiate connection. Please try again.',
          variant: 'destructive'
        });
        setConnectingChannel(null);
      }
    } catch (error) {
      toast({
        title: 'Connection Failed',
        description: 'Unable to connect. Please try again.',
        variant: 'destructive'
      });
      setConnectingChannel(null);
    }
  };

  const handleDisconnect = async (channelType) => {
    setDisconnectingChannel(channelType);
    try {
      const response = await fetch(API_URL + '/api/channels/' + channelType + '/disconnect', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (response.ok) {
        toast({
          title: 'Disconnected',
          description: channelType.charAt(0).toUpperCase() + channelType.slice(1) + ' has been disconnected.'
        });
        fetchChannelsStatus();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to disconnect. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setDisconnectingChannel(null);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'connected') {
      return <Badge className="bg-green-100 text-green-700"><Check className="h-3 w-3 mr-1" />Connected</Badge>;
    } else if (status === 'setup_required') {
      return <Badge className="bg-yellow-100 text-yellow-700"><AlertCircle className="h-3 w-3 mr-1" />Setup Required</Badge>;
    } else if (status === 'coming_soon') {
      return <Badge variant="secondary">Coming Soon</Badge>;
    }
    return <Badge variant="outline" className="text-gray-500">Not Connected</Badge>;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Channels</h1>
          <p className="text-gray-600 mt-1">Connect your messaging platforms to automate conversations</p>
        </div>

        <div className="space-y-6">
          {/* Instagram Channel */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-xl flex items-center justify-center">
                    <Instagram className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Instagram</CardTitle>
                    <CardDescription>Automate DMs, comments and story replies</CardDescription>
                  </div>
                </div>
                {getStatusBadge(channels.instagram.status)}
              </div>
            </CardHeader>
            <CardContent>
              {channels.instagram.status === 'connected' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-full flex items-center justify-center">
                        <Instagram className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">@{channels.instagram.username}</p>
                        <p className="text-sm text-gray-500">Status: Active</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDisconnect('instagram')}
                      disabled={disconnectingChannel === 'instagram'}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      {disconnectingChannel === 'instagram' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Disconnect'
                      )}
                    </Button>
                  </div>
                </div>
              ) : channels.instagram.status === 'setup_required' ? (
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-800">No Instagram Business Account Found</p>
                        <p className="text-sm text-yellow-700 mt-1">
                          To enable automation:
                        </p>
                        <ul className="text-sm text-yellow-700 mt-2 list-disc list-inside space-y-1">
                          <li>Switch your Instagram to a Business or Creator account</li>
                          <li>Link it to a Facebook Page you manage</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => handleConnect('instagram')} disabled={connectingChannel === 'instagram'}>
                    {connectingChannel === 'instagram' ? (
                      <span className="flex items-center"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</span>
                    ) : (
                      'Try Again'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Connect your Instagram Business or Creator account to automate DMs, respond to comments, and engage with story replies.
                  </p>
                  <p className="text-xs text-gray-500">
                    Instagram must be a Business or Creator account.
                  </p>
                  <Button 
                    onClick={() => handleConnect('instagram')} 
                    disabled={connectingChannel === 'instagram'}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    {connectingChannel === 'instagram' ? (
                      <span className="flex items-center"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</span>
                    ) : (
                      <span className="flex items-center"><Instagram className="h-4 w-4 mr-2" />Connect via Meta</span>
                    )}
                  </Button>
                  <p className="text-xs text-gray-400">
                    ChatFlow uses official Meta APIs for secure authentication.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Facebook Messenger Channel */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <Facebook className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Facebook Messenger</CardTitle>
                    <CardDescription>Automate Messenger conversations</CardDescription>
                  </div>
                </div>
                {getStatusBadge(channels.messenger.status)}
              </div>
            </CardHeader>
            <CardContent>
              {channels.messenger.status === 'connected' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                        <Facebook className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{channels.messenger.page_name}</p>
                        <p className="text-sm text-gray-500">Status: Active</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDisconnect('messenger')}
                      disabled={disconnectingChannel === 'messenger'}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      {disconnectingChannel === 'messenger' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Disconnect'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Connect your Facebook Page to automate Messenger conversations and respond to customer inquiries.
                  </p>
                  <Button 
                    onClick={() => handleConnect('messenger')} 
                    disabled={connectingChannel === 'messenger'}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {connectingChannel === 'messenger' ? (
                      <span className="flex items-center"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</span>
                    ) : (
                      <span className="flex items-center"><Facebook className="h-4 w-4 mr-2" />Connect via Meta</span>
                    )}
                  </Button>
                  <p className="text-xs text-gray-400">
                    ChatFlow uses official Meta APIs for secure authentication.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* WhatsApp Channel - Coming Soon */}
          <Card className="opacity-60">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <MessageCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">WhatsApp Business</CardTitle>
                    <CardDescription>Automate WhatsApp conversations</CardDescription>
                  </div>
                </div>
                {getStatusBadge('coming_soon')}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  WhatsApp Business API integration is coming soon. Stay tuned for updates.
                </p>
                <Button disabled variant="outline">
                  Coming Soon
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Channels;
